import { customElement, resolve, type IDisposable, IEventAggregator } from 'aurelia';
import { I18N, Signals } from '@aurelia/i18n';
import { route } from '@aurelia/router';
import template from './help-view.html';
import handbookContent from '../content/handbook-content.json';
import { hasPermission, PERMISSIONS_CHANGED_CHANNEL } from '../services/permissions.service';
import { LANGUAGE_CHANGED_CHANNEL } from '../services/languages';

type HandbookLanguage = 'en' | 'de';

interface LocalizedText {
  en: string;
  de?: string;
}

type HandbookBlock =
  | { type: 'paragraph' | 'tip' | 'warning'; text: LocalizedText }
  | { type: 'bullets' | 'steps'; items: LocalizedText[] };

interface HandbookSection {
  id: string;
  audience: 'user' | 'admin';
  title: LocalizedText;
  keywords: string[];
  blocks: HandbookBlock[];
}

interface HandbookDocument {
  version: string;
  title: LocalizedText;
  subtitle: LocalizedText;
  sections: HandbookSection[];
}

const handbook = handbookContent as HandbookDocument;

@route({ path: 'help', title: 'Help' })
@customElement({ name: 'help-view', template })
export class HelpView {
  private readonly i18n: I18N = resolve(I18N);
  private readonly ea: IEventAggregator = resolve(IEventAggregator);
  private _localeSub: IDisposable | null = null;
  private _languageSub: IDisposable | null = null;
  private _permissionsSub: IDisposable | null = null;
  private readonly searchIndex = new Map<string, string>(
    handbook.sections.map(section => [section.id, `${JSON.stringify(section)} ${section.keywords.join(' ')}`.toLocaleLowerCase()]),
  );

  public readonly sections = handbook.sections;
  public language: HandbookLanguage = 'en';
  public searchTerm = '';
  public hasAdminAccess = false;

  public get handbookTitle(): LocalizedText {
    return handbook.title;
  }

  public get handbookSubtitle(): LocalizedText {
    return handbook.subtitle;
  }

  public binding(): void {
    this.language = this.toLanguage(localStorage.getItem('radish-language') ?? this.i18n.getLocale());
    this.hasAdminAccess = hasPermission('maintenance.access');
    this._localeSub = this.ea.subscribe(Signals.I18N_EA_CHANNEL, (locales: { newLocale?: string }) => {
      this.language = this.toLanguage(locales.newLocale ?? this.i18n.getLocale());
    });
    this._languageSub = this.ea.subscribe(LANGUAGE_CHANGED_CHANNEL, (language: string) => {
      this.language = this.toLanguage(language);
    });
    this._permissionsSub = this.ea.subscribe(PERMISSIONS_CHANGED_CHANNEL, () => {
      this.hasAdminAccess = hasPermission('maintenance.access');
    });
  }

  public unbinding(): void {
    this._localeSub?.dispose();
    this._languageSub?.dispose();
    this._permissionsSub?.dispose();
  }

  public get visibleSections(): HandbookSection[] {
    const term = this.searchTerm.trim().toLocaleLowerCase();
    return this.sections.filter(section => {
      if (section.audience === 'admin' && !this.hasAdminAccess) return false;
      return !term || (this.searchIndex.get(section.id)?.includes(term) ?? false);
    });
  }

  public localized(value: LocalizedText): string {
    return value[this.language] ?? value.en;
  }

  public clearSearch(): void {
    this.searchTerm = '';
  }

  public print(): void {
    window.print();
  }

  public scrollToSection(id: string, event: Event): void {
    event.preventDefault();
    const target = document.getElementById(id);
    const scrollContainer = target?.closest<HTMLElement>('.help-scroll');
    if (!target || !scrollContainer) return;

    const top = scrollContainer.scrollTop
      + target.getBoundingClientRect().top
      - scrollContainer.getBoundingClientRect().top
      - 20;
    scrollContainer.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  private toLanguage(value: string): HandbookLanguage {
    return value.toLocaleLowerCase().startsWith('de') ? 'de' : 'en';
  }
}
